import { useState, type ReactNode } from "react";
import { DateTime } from "luxon";
import { SlidersHorizontal, ChevronDown, ChevronRight, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getPriorYearRange } from "@/lib/periods";
import { humanizeSlug } from "@/lib/metrics";
import type { FilterState, PeriodKey, ScopeFilters, ScopeOptions } from "@/types/filters";
import type { PeriodState } from "@/components/dashboard/PeriodBar";

const PERIOD_CHIP: Record<PeriodKey, string> = {
  T1W: "T1W · Last full week",
  T4W: "T4W · Last 4 full weeks",
  T13W: "T13W · Last 13 full weeks",
  T52W: "T52W · Last 52 full weeks",
  PM: "PM · Previous month",
  MTD: "MTD · Month to date",
  YTD: "YTD · Year to date",
};

interface RangeParts {
  startDow: string;
  startDate: string;
  endDow: string;
  endDate: string;
  /** The year the window belongs to -- its END year, used as the row key. */
  year: string;
}

// The weekday matters because half the periods are not week-aligned. T1W-T52W
// always run Monday to Sunday, so the weekdays confirm it at a glance; PM, MTD
// and YTD follow the calendar and land wherever the month does, which is the
// case that used to need working out from the numbers.
function rangeParts(start: string, end: string): RangeParts {
  const s = DateTime.fromISO(start);
  const e = DateTime.fromISO(end);
  const sameYear = s.hasSame(e, "year");
  return {
    startDow: s.toFormat("ccc"),
    // The year normally lives in the row key, so the dates carry none. A window
    // that straddles two years (T52W, or YTD in January) is the exception: the
    // start date keeps its own year, or "Sep 1 ~ Aug 30" would look backwards.
    startDate: sameYear ? s.toFormat("MMM d") : s.toFormat("MMM d, yyyy"),
    endDow: e.toFormat("ccc"),
    endDate: e.toFormat("MMM d"),
    year: e.toFormat("yyyy"),
  };
}

// One row of the range grid, emitted as a FRAGMENT so its four cells land in the
// parent grid rather than in a nested box. That is what lets the current and
// prior-year rows share columns -- and sharing the "~" column is the whole
// point: on MTD the two rows read "Sep 1 ~ Sep 7" and "Sep 1 ~ Sep 7", and only
// the stacked weekdays show that one runs Tue-Mon and the other Mon-Sun.
function RangeRow({
  rowKey,
  parts,
  muted,
}: {
  rowKey: string;
  parts: RangeParts;
  muted?: boolean;
}) {
  const text = muted ? "text-[10.5px] font-medium text-gray-600" : "text-[12.5px] font-semibold";
  const dow = muted ? "text-gray-500" : "text-gray-600";
  const tint = muted ? undefined : { color: "var(--brand-dark-teal)" };
  const day = (date: string, weekday: string) => (
    <>
      <span className="tabular-nums">{date}</span>{" "}
      <span className={`text-[0.82em] font-medium ${dow}`}>({weekday})</span>
    </>
  );
  return (
    <>
      <span className="text-[10.5px] font-semibold tabular-nums text-gray-500">{rowKey}</span>
      {/* Right-aligned so the "~" lands in the same place on both rows despite
          "Aug 31" and "Sep 1" being different widths. */}
      <span className={`justify-self-end whitespace-nowrap ${text}`} style={tint}>
        {day(parts.startDate, parts.startDow)}
      </span>
      <span className={muted ? "text-gray-400" : "text-gray-500"}>~</span>
      <span className={`whitespace-nowrap ${text}`} style={tint}>
        {day(parts.endDate, parts.endDow)}
      </span>
    </>
  );
}

interface FilterBarProps {
  activeTabLabel: string;
  filters: FilterState;
  options: ScopeOptions;
  periodState: PeriodState | null;
  siteCount: number;
  dvmCount: number;
  /** Counts of an unfetched payload are zero, not "none selected" -- show a dash. */
  countsPending: boolean;
  filtersOpen: boolean;
  onToggleFilters: () => void;
  setScope: (patch: Partial<ScopeFilters>) => void;
  setAppointmentTypes: (slugs: string[]) => void;
  setLaunchedBefore: (date: string | null) => void;
  scopeSlot: ReactNode;
  periodSlot: ReactNode;
}

interface Chip {
  key: string;
  label: string;
  /** The prefix the label no longer carries — "Country: USA" on hover. */
  title: string;
  onRemove: () => void;
}

// A two-stop shadow: a 1px contact shadow for the edge and a wide soft one
// for the lift, which is what makes the header cards read as floating over
// the content that scrolls beneath them rather than as inset panels.
//
// Inline rather than an arbitrary-value class because the multi-stop
// shadow class silently generated two TRANSPARENT shadows -- the computed
// box-shadow came back as "rgba(0, 0, 0, 0) 0px 0px 0px 0px, ..." and
// nothing rendered at all.
const GLASS_SHADOW = {
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04), 0 10px 24px -12px rgba(0, 0, 0, 0.18)",
};

/** Appointment types beyond this collapse behind a "+N more" toggle. */
const TYPE_CHIP_LIMIT = 3;

export function FilterBar({
  activeTabLabel,
  filters,
  options,
  periodState,
  siteCount,
  dvmCount,
  countsPending,
  filtersOpen,
  onToggleFilters,
  setScope,
  setAppointmentTypes,
  setLaunchedBefore,
  scopeSlot,
  periodSlot,
}: FilterBarProps) {
  const { scope, appointmentTypes, launchedBefore } = filters;
  const [typesExpanded, setTypesExpanded] = useState(false);

  const siteName = scope.locationId
    ? (options.sites.find((s) => s.id === scope.locationId)?.name ?? scope.locationId)
    : null;
  const dvmName = scope.resourceId
    ? (options.dvms.find((d) => d.id === scope.resourceId)?.name ?? scope.resourceId)
    : null;

  // Only chips for filters narrowed from their "All" default — keeps the bar
  // uncluttered. Labels carry the value alone: rendered in hierarchy order with
  // separators between them, the path says which tier each one is, and the
  // "Country: "/"Division: " prefixes were most of each chip's width. The tier
  // name survives on hover for anyone who wants it spelled out.
  const scopeChips: Chip[] = [
    scope.countryCode && {
      key: "countryCode",
      label: scope.countryCode,
      title: `Country: ${scope.countryCode}`,
      onRemove: () => setScope({ countryCode: null }),
    },
    scope.groupName && {
      key: "groupName",
      label: scope.groupName,
      title: `Group: ${scope.groupName}`,
      onRemove: () => setScope({ groupName: null }),
    },
    scope.divisionName && {
      key: "divisionName",
      label: scope.divisionName,
      title: `Division: ${scope.divisionName}`,
      onRemove: () => setScope({ divisionName: null }),
    },
    scope.locationId && {
      key: "locationId",
      label: `${scope.locationId} · ${siteName}`,
      title: `Site: ${scope.locationId} - ${siteName}`,
      onRemove: () => setScope({ locationId: null }),
    },
    scope.resourceId && {
      key: "resourceId",
      label: dvmName ?? "",
      title: `DVM: ${dvmName}`,
      onRemove: () => setScope({ resourceId: null }),
    },
  ].filter((c): c is Chip => Boolean(c));

  // Appointment types are a separate axis, not a level of the hierarchy, so they
  // sit outside the path and are tinted differently — without the "Type: "
  // prefix a chip reading "Dental" is otherwise indistinguishable from a
  // division name.
  const typeChips: Chip[] = appointmentTypes.map((slug) => ({
    key: `type:${slug}`,
    label: humanizeSlug(slug),
    title: `Appointment type: ${humanizeSlug(slug)}`,
    onRemove: () => setAppointmentTypes(appointmentTypes.filter((s) => s !== slug)),
  }));
  const visibleTypeChips = typesExpanded ? typeChips : typeChips.slice(0, TYPE_CHIP_LIMIT);
  const hiddenTypeCount = typeChips.length - visibleTypeChips.length;

  // Shows the range actually computed over — no longer trimmed to complete
  // weeks for display, which used to make a calendar period report dates it
  // hadn't used.
  const cyBounds = periodState
    ? { startDate: periodState.customStart, endDate: periodState.customEnd }
    : null;
  const cyRange = cyBounds ? rangeParts(cyBounds.startDate, cyBounds.endDate) : null;
  // Trailing periods match the prior year by week number rather than by date,
  // so the PY dates aren't guessable from the CY range — worth showing. Null
  // when there is no counterpart at all (a lone week 53).
  const pyRange = (() => {
    if (!cyBounds || !periodState) return null;
    const py = getPriorYearRange(cyBounds, periodState.period);
    return py ? rangeParts(py.startDate, py.endDate) : null;
  })();
  // A lone week 53 has no counterpart at all, and saying so is more use than an
  // empty row where a date should be.
  const pyMissing = Boolean(
    cyBounds && periodState && !getPriorYearRange(cyBounds, periodState.period)
  );

  return (
    <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm">
      {/* Summary row.
          Two columns that wrap ONLY when they genuinely cannot fit, which is a
          narrower promise than it sounds. The chip list inside the left column
          wraps internally, so its length changes the column's HEIGHT and never
          its width -- which is what stops a long chip list pushing the cards
          onto a second line. That was the failure that made this row
          flex-nowrap originally: the stable controls moved and the list that
          displaced them did not.
          min-w-[160px] is what replaces flex-nowrap. Without a floor the left
          column collapsed to 0px below ~1000px, putting the tab label and chips
          BEHIND the cards while the right column still overflowed by 14px. With
          it, the right column wraps to its own line instead.
          160px is picked so the wrap threshold lands just BELOW 1024: at that
          width the row still fits on one line, which is where it has always
          been, and only genuinely cramped windows get the two-line layout. It
          is also enough for the tab label, the widest thing that must not
          truncate. */}
      {/* Chips carry max-w AND min-w-0: the cap stops a long site name taking the
          row, and min-w-0 lets it shrink below that cap when the column is
          narrow. Without the latter a 200px chip in a 174px column pushed a
          horizontal scrollbar onto the page at a 1024 viewport. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-8 py-3">
        <div className="min-w-[160px] flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500 mb-1">
            {activeTabLabel}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {scopeChips.map((chip, i) => (
              // Separator and chip wrap as one unit -- as two siblings the
              // chevron could end a line with nothing after it.
              <span key={chip.key} className="inline-flex min-w-0 items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 text-gray-300" />}
                <Badge
                  variant="secondary"
                  title={chip.title}
                  className="h-6 min-w-0 max-w-[200px] gap-1 pr-1 text-[11px] font-medium bg-[color-mix(in_srgb,var(--brand-cream)_50%,white)] border border-black/10"
                >
                  <span className="truncate">{chip.label}</span>
                  <button
                    onClick={chip.onRemove}
                    aria-label={`Remove ${chip.title}`}
                    className="shrink-0 rounded p-0.5 hover:bg-black/10"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </span>
            ))}

            {scopeChips.length > 0 && typeChips.length > 0 && (
              <span className="mx-1 h-4 w-px shrink-0 bg-black/10" />
            )}

            {visibleTypeChips.map((chip) => (
              <Badge
                key={chip.key}
                variant="secondary"
                title={chip.title}
                className="h-6 min-w-0 max-w-[200px] gap-1 pr-1 text-[11px] font-medium bg-[color-mix(in_srgb,var(--brand-lilac-light)_45%,white)] border border-[color-mix(in_srgb,var(--brand-lilac-dark)_25%,transparent)]"
              >
                <span className="truncate">{chip.label}</span>
                <button
                  onClick={chip.onRemove}
                  aria-label={`Remove ${chip.title}`}
                  className="shrink-0 rounded p-0.5 hover:bg-black/10"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}

            {typeChips.length > TYPE_CHIP_LIMIT && (
              <button
                onClick={() => setTypesExpanded((o) => !o)}
                className="h-6 shrink-0 rounded-md border border-black/10 px-2 text-[11px] font-medium text-gray-500 hover:bg-black/[0.04]"
              >
                {typesExpanded ? "Show fewer" : `+${hiddenTypeCount} more`}
              </button>
            )}

            {/* Its own tint, and always shown while set: this one changes which
                sites every figure on the page is built from, including the NVA
                benchmark, so it is the last chip that should be easy to miss. */}
            {launchedBefore && (
              <Badge
                variant="secondary"
                title={`Sites live on the new PIMS by ${DateTime.fromISO(launchedBefore).toFormat("MMM d, yyyy")}`}
                className="h-6 shrink-0 gap-1 pr-1 text-[11px] font-medium bg-[color-mix(in_srgb,var(--brand-orange-light)_45%,white)] border border-[color-mix(in_srgb,var(--brand-orange-dark)_25%,transparent)]"
              >
                <span className="truncate">
                  Launched ≤ {DateTime.fromISO(launchedBefore).toFormat("MMM d, yyyy")}
                </span>
                <button
                  onClick={() => setLaunchedBefore(null)}
                  aria-label="Remove PIMS launch date filter"
                  className="shrink-0 rounded p-0.5 hover:bg-black/10"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}

            {periodState && (
              // min-w-0 + truncate rather than shrink-0: the longest label,
              // "T52W · Last 52 full weeks", is 141px, and at a 1024 viewport
              // with T52W selected the range card leaves this column 133px. It
              // fitted until the range card grew. The title keeps the full text
              // reachable in the one case it clips.
              <span
                title={PERIOD_CHIP[periodState.period]}
                className="inline-flex min-w-0 items-center truncate rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                style={{ background: "var(--brand-dark-teal)" }}
              >
                {PERIOD_CHIP[periodState.period]}
              </span>
            )}
          </div>
        </div>

        {/* Wraps too. Even on its own line the two cards plus the Filters
            button need ~570px, which a 820px window does not have once the nav
            rail and padding are taken out -- so the button drops below the
            cards rather than overflowing the page. */}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-x-5 gap-y-2">
          {cyRange && (
            <div
              className="flex flex-col items-start rounded-xl border border-black/[0.06] bg-white/70 px-3.5 py-1.5 backdrop-blur-md"
              style={GLASS_SHADOW}
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.6px] text-gray-400">
                Selected Range
              </div>
              {/* leading-tight because items-baseline reserves a 24px row for a
                  20px line box, which pushed the card 8px taller than the
                  Selection card beside it for no visible reason. */}
              <div className="grid grid-cols-[auto_auto_auto_auto] items-baseline gap-x-1.5 leading-tight">
                <RangeRow rowKey={cyRange.year} parts={cyRange} />
                {pyRange && <RangeRow rowKey="PY" parts={pyRange} muted />}
              </div>
              {pyMissing && (
                <div className="mt-0.5 text-[11px] font-medium text-gray-400">
                  No prior-year match (week 53)
                </div>
              )}
            </div>
          )}
          {/* items-start on both cards: the range grid reads left-to-right from
              its row keys, so centring it would leave the label floating over
              ragged content -- and once one card is left-aligned the other has
              to be, or the pair looks unrelated. */}
          <div
            className="flex flex-col items-start rounded-xl border border-black/[0.06] bg-white/70 px-3.5 py-1.5 backdrop-blur-md"
            style={GLASS_SHADOW}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.6px] text-gray-400">
              Selection
            </div>
            <div className="flex items-baseline gap-3">
              <div>
                <span
                  className="text-[18px] font-semibold tabular-nums"
                  style={{ color: "var(--brand-dark-teal)" }}
                >
                  {countsPending ? "—" : siteCount}
                </span>{" "}
                <span className="text-[11px] text-gray-400">
                  {siteCount === 1 && !countsPending ? "site" : "sites"}
                </span>
              </div>
              <div>
                <span
                  className="text-[18px] font-semibold tabular-nums"
                  style={{ color: "var(--brand-dark-teal)" }}
                >
                  {countsPending ? "—" : dvmCount}
                </span>{" "}
                <span className="text-[11px] text-gray-400">
                  {dvmCount === 1 && !countsPending ? "DVM" : "DVMs"}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={onToggleFilters}
            className={[
              "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-[13px] font-medium transition-colors",
              filtersOpen
                ? "border-[var(--brand-dark-teal)] text-[var(--brand-dark-teal)] bg-white"
                : "border-black/15 text-gray-600 bg-white hover:bg-gray-50",
            ].join(" ")}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            <ChevronDown
              className="h-4 w-4 transition-transform duration-200"
              style={{ transform: filtersOpen ? "rotate(180deg)" : "none" }}
            />
          </button>
        </div>
      </div>

      {/* Collapsible drawer */}
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-200 ease-out"
        style={{ maxHeight: filtersOpen ? 600 : 0, opacity: filtersOpen ? 1 : 0 }}
      >
        <div className="border-t border-black/5 bg-[color-mix(in_srgb,var(--brand-cream)_30%,white)] px-8 pt-4 pb-1">
          {scopeSlot}
          {periodSlot}
        </div>
      </div>

      {/* Brand accent stripe — divides the pinned filter bar from content */}
      <div className="flex h-[3px] w-full overflow-hidden">
        <div className="flex-1" style={{ background: "var(--brand-dark-teal)" }} />
        <div className="flex-1" style={{ background: "var(--brand-orange-light)" }} />
        <div className="flex-1" style={{ background: "var(--brand-lilac-light)" }} />
        <div className="flex-1" style={{ background: "var(--brand-lilac-dark)" }} />
        <div className="flex-1" style={{ background: "var(--brand-green-mid)" }} />
      </div>
    </div>
  );
}
